/**
 * Thin wrapper around Google Analytics 4.
 *
 * - Safe to call before gtag loads (noop if missing).
 * - Sanitises values: strings trimmed, booleans → 0/1, unknowns dropped.
 * - Single import surface so we don't sprinkle `window.gtag` checks across the app.
 */
export function trackEvent(name, params) {
  if (typeof window === "undefined") return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  try {
    const clean = {};
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (typeof v === "boolean") { clean[k] = v ? 1 : 0; return; }
      if (typeof v === "string") { clean[k] = v.slice(0, 100); return; }
      if (typeof v === "number") { clean[k] = v; return; }
    });
    gtag("event", name, clean);
  } catch (e) { /* never crash the app for analytics */ }
}

// Named convenience helpers — keep a single source of truth for event names
// so reports & funnels match what the code emits.
export const ga = {
  bookingStarted: (p) => trackEvent("booking_started", p),
  bookingStep2:   (p) => trackEvent("booking_step_2", p),
  paymentInitiated: (p) => trackEvent("payment_initiated", p),
  paymentSuccess: (p) => trackEvent("payment_success", p),
  referralShared: (p) => trackEvent("referral_shared", p),

  /**
   * GA4 enhanced ecommerce `purchase` event — unlocks the Monetization
   * report in GA4 (revenue by source/medium/campaign, ARPU, etc.).
   * Idempotent per `transactionId` via localStorage so Strict-Mode mounts
   * + page reloads don't double-count.
   *
   * Schema docs:
   * https://developers.google.com/tag-platform/gtagjs/reference/events#purchase
   */
  purchase: ({ transactionId, valueRupees, priority, conditionSlug }) => {
    if (!transactionId || !valueRupees) return;
    if (typeof window === "undefined") return;
    const KEY = "noi_ga_purchases";
    try {
      const seen = JSON.parse(localStorage.getItem(KEY) || "[]");
      if (seen.includes(transactionId)) return;
      seen.push(transactionId);
      // Cap stored ids — last 100 is plenty.
      localStorage.setItem(KEY, JSON.stringify(seen.slice(-100)));
    } catch (e) { /* private mode etc — fall through and just emit once */ }

    if (typeof window.gtag !== "function") return;
    try {
      window.gtag("event", "purchase", {
        transaction_id: transactionId,
        value: Number(valueRupees),
        currency: "INR",
        items: [{
          item_id: conditionSlug || "second-opinion",
          item_name: "Neuro second opinion",
          item_category: priority || "standard",
          price: Number(valueRupees),
          quantity: 1,
        }],
      });
    } catch (e) { /* swallow analytics errors */ }
  },
};
